import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { createElement } from "commonAssets/lib/createElement";
import { modal } from "commonAssets/lib/modal";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import { i18n, loading, outputDiv } from "commonAssets/js/constants";
import formDefault from "../../docs/formDefault.json";
import claimDeadlineAlertBody from "../../docs/html/claim_deadline_alert_body.html?raw";
import claimDeadlineAlertForm from "../../docs/html/claim_deadline_alert_form.html?raw";
import generalInstructionsBody from "../../docs/html/general_instructions_body.html?raw";
import contactInformationBody from "../../docs/html/contact_information_body.html?raw";

const htmlAssets = {
    "./html/claim_deadline_alert_body.html": claimDeadlineAlertBody,
    "./html/claim_deadline_alert_form.html": claimDeadlineAlertForm,
    "./html/general_instructions_body.html": generalInstructionsBody,
    "./html/contact_information_body.html": contactInformationBody,
};

const toBool = (value) => {
    if (value === true || value === 1 || value === "1") {
        return true;
    }

    if (value === false || value === 0 || value === "0") {
        return false;
    }

    return undefined;
};

const getClaimAccess = (resultData) => {
    const envelope = resultData?.class_member ?? resultData;

    return {
        isClaimDeficient:
            toBool(
                envelope?.is_claim_deficient ??
                    resultData?.is_claim_deficient ??
                    envelope?.is_deficient ??
                    resultData?.is_deficient
            ) === true,
        isClaimEditable:
            toBool(
                envelope?.is_claim_editable ??
                    resultData?.is_claim_editable ??
                    envelope?.can_edit ??
                    resultData?.can_edit
            ) === true,
        deficiencyReason:
            envelope?.deficiency_reason ??
            envelope?.deficiency_reasons ??
            resultData?.deficiency_reason ??
            resultData?.deficiency_reasons ??
            "",
    };
};

export class customSubmitClaim extends SubmitClaim {
    async form() {
        const baseUrl = new URL(
            "../../docs/formDefault.json",
            window.location.href
        ).href;
        const config = await SubmitClaim.resolveHtmlFields(formDefault, {
            baseUrl,
            assets: htmlAssets,
        });
        const form = config.claim_form[this.lang];
        this.init_claim(form);
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claimDeadline;
                if (Boolean(result.data.claim_deadline)) {
                    claimDeadline = result.data.claim_deadline;
                }

                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                if (form.form_options.notice_confirmation.display === true) {
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    const zeroZeroId = document.getElementById("zero_zero_id");
                    const confirmationCode =
                        document.getElementById("confirmation_code");

                    [zeroZeroId, confirmationCode].forEach((input) => {
                        input.addEventListener("change", validateField);
                    });

                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                        });

                    document
                        .getElementById("guard")
                        .addEventListener("submit", (event) => {
                            event.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zeroZeroId.value.toUpperCase(),
                                confirmation_code: confirmationCode.value,
                            };

                            clearMessage();

                            [zeroZeroId, confirmationCode].forEach((input) => {
                                valid = validateField(input) && valid;
                            });
                            if (!valid) return;

                            loadingGif(loading, "show");

                            this.claim.api
                                .classMemberPost({}, member)
                                .then((memberResult) => {
                                    if (memberResult.data.error) {
                                        toggleFieldError(zeroZeroId, true, "");
                                        toggleFieldError(
                                            confirmationCode,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: memberResult }
                                            )
                                        );
                                        this.errorHandler.display_modal(
                                            memberResult
                                        );
                                        return;
                                    }

                                    const access = getClaimAccess(
                                        memberResult.data
                                    );

                                    if (
                                        !access.isClaimDeficient ||
                                        !access.isClaimEditable
                                    ) {
                                        loadingGif(loading, "hide", 250);
                                        modal(
                                            i18n.getTranslation("error"),
                                            "<p class='mb-0 text-center'>This portal is not open to new claims. If you received a deficiency notice, please contact the settlement administrator for assistance.</p>"
                                        );
                                        return;
                                    }

                                    this.claim.deficiency_reason =
                                        access.deficiencyReason;
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    const environment =
                                        this.claim.data.domain.split(".")[0];

                                    if (memberResult.data.claim_id) {
                                        this.claim.data.claim_id =
                                            memberResult.data.claim_id;
                                        this.claim.class_member =
                                            memberResult.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                memberResult.data,
                                                claimDeadline
                                            );
                                            return;
                                        }
                                    }

                                    this.populate_claim_form(
                                        form,
                                        container,
                                        memberResult.data,
                                        claimDeadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error("Error loading class member", {
                                            cause: error,
                                        })
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", {
                        cause: error,
                    })
                );
                this.errorHandler.display(error);
            });
    }

    populate_claim_form(form, container, classMember, claimDeadline) {
        const access = getClaimAccess(classMember);

        if (!access.isClaimDeficient || !access.isClaimEditable) {
            this.errorHandler.display({
                status: 403,
                error: "This claim is no longer editable. Please contact the settlement administrator for assistance.",
            });
            return;
        }

        this.claim.deficiency_reason = access.deficiencyReason;
        super.populate_claim_form(form, container, classMember, claimDeadline);
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new customSubmitClaim(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }
}
